import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { MainAppReducer } from 'Types';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import { isCapacitorNative } from 'Utils/Constants';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import {
  NotificationAudienceType,
  NotificationPlatform,
  NotificationStatus,
  NotificationSurface,
  type AppNotification,
} from 'Types/notifications';
import { getCurrentAppVersion, isAppVersionBehind } from 'Utils/appVersion';
import {
  fetchActiveAppNotification,
  getLocalNotificationState,
  recordNotificationCtaClick,
  recordNotificationDismiss,
  recordNotificationImpression,
  saveLocalNotificationState,
} from 'Utils/notifications';
import AnnouncementModal from './AnnouncementModal';

const SAFE_ROUTES = ['/dashboard', '/roadmap', '/repertoire', '/settings', '/welcome'];
const PREVIEW_QUERY_PARAM = 'previewNotificationModal';

const isSafeRoute = (pathname: string) =>
  SAFE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const buildPreviewNotification = (): AppNotification => {
  const timestamp = new Date().toISOString();

  return {
    id: -1,
    slug: 'local-preview-app-update',
    surface: NotificationSurface.APP_MODAL,
    status: NotificationStatus.ACTIVE,
    title: 'A cleaner way to surface what changed.',
    body: 'This is a local preview of the new release modal style. It uses the same visual language as the roadmap and repertoire surfaces, but will eventually be driven by active notifications from the backend admin.',
    image_url: null,
    cta_label: 'Looks Good',
    cta_url: null,
    dismissible: true,
    priority: 9999,
    starts_at: null,
    ends_at: null,
    required_feature_flag: null,
    audience_type: NotificationAudienceType.ALL_USERS,
    audience_filters: null,
    platform: NotificationPlatform.ALL,
    app_version: null,
    enforce: false,
    created_by: 'local-preview',
    updated_by: 'local-preview',
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: null,
  };
};

const AnnouncementModalHost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuthWrapperContext();
  const { userDataFetched } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const userId = auth.user?.attributes?.sub;
  const announcementModalEnabled = isFeatureEnabled(FeatureFlag.USE_ANNOUNCEMENT_MODAL);

  const [notification, setNotification] = useState<AppNotification | null>(null);
  const [open, setOpen] = useState(false);
  const [currentAppVersion, setCurrentAppVersion] = useState<string | null>(null);
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const previewRequested = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get(PREVIEW_QUERY_PARAM);

    return value === '1' || value === 'true';
  }, [location.search]);
  const previewMode = announcementModalEnabled && previewRequested;

  const canFetch = useMemo(
    () =>
      auth.isAuthenticated &&
      userDataFetched &&
      !!userId &&
      online &&
      isSafeRoute(location.pathname),
    [auth.isAuthenticated, location.pathname, online, userDataFetched, userId],
  );

  const requiredFeatureFlagSatisfied = useCallback((requiredFeatureFlag?: string | null) => {
    if (!requiredFeatureFlag) return true;

    if ((Object.values(FeatureFlag) as string[]).includes(requiredFeatureFlag)) {
      return isFeatureEnabled(requiredFeatureFlag as FeatureFlag);
    }

    return true;
  }, []);

  const clearPreviewQueryParam = useCallback(() => {
    if (!previewRequested) return;

    const params = new URLSearchParams(location.search);
    params.delete(PREVIEW_QUERY_PARAM);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, previewRequested]);

  const syncCurrentAppVersion = useCallback(async () => {
    const version = await getCurrentAppVersion();
    setCurrentAppVersion((previous) => (previous === version ? previous : version));
    return version;
  }, []);

  const loadNotification = useCallback(async () => {
    if (previewMode) {
      setNotification(buildPreviewNotification());
      setOpen(true);
      return;
    }

    if (!announcementModalEnabled || !canFetch || !userId) {
      setOpen(false);
      setNotification(null);
      return;
    }

    try {
      const appVersion = await syncCurrentAppVersion();
      const activeNotification = await fetchActiveAppNotification();

      if (!activeNotification) {
        setNotification(null);
        setOpen(false);
        return;
      }

      if (!requiredFeatureFlagSatisfied(activeNotification.required_feature_flag)) {
        setNotification(null);
        setOpen(false);
        return;
      }

      const blocksUntilUpgrade =
        !!activeNotification.enforce &&
        isAppVersionBehind(appVersion, activeNotification.app_version);

      const localState = await getLocalNotificationState(userId, activeNotification.id);
      if (
        localState?.dismissedAt ||
        localState?.completedAt ||
        (!blocksUntilUpgrade && localState?.ctaClickedAt)
      ) {
        setNotification(null);
        setOpen(false);
        return;
      }

      setNotification(activeNotification);
      setOpen(true);
    } catch (error) {
      console.error('Failed to load active app notification:', error);
    }
  }, [
    announcementModalEnabled,
    canFetch,
    previewMode,
    requiredFeatureFlagSatisfied,
    syncCurrentAppVersion,
    userId,
  ]);

  useEffect(() => {
    void loadNotification();
  }, [loadNotification]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
    };
    const handleOffline = () => {
      setOnline(false);
      setOpen(false);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void loadNotification();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadNotification]);

  useEffect(() => {
    if (!announcementModalEnabled || previewMode || !notification || !open || !userId) return;

    let cancelled = false;

    const syncImpression = async () => {
      const timestamp = new Date().toISOString();
      const localState = await getLocalNotificationState(userId, notification.id);

      if (cancelled) return;

      await saveLocalNotificationState(userId, notification.id, {
        lastShownAt: timestamp,
      });

      if (localState?.impressionRecordedAt) {
        return;
      }

      try {
        await recordNotificationImpression(notification.id);
        if (!cancelled) {
          await saveLocalNotificationState(userId, notification.id, {
            impressionRecordedAt: timestamp,
          });
        }
      } catch (error) {
        console.warn('Failed to record notification impression:', error);
      }
    };

    void syncImpression();

    return () => {
      cancelled = true;
    };
  }, [announcementModalEnabled, notification, open, previewMode, userId]);

  const blocksUntilUpgrade = useMemo(() => {
    if (!notification) return false;

    return (
      !!notification.enforce && isAppVersionBehind(currentAppVersion, notification.app_version)
    );
  }, [currentAppVersion, notification]);

  const canDismiss = useMemo(() => {
    if (!notification) return false;
    return notification.dismissible && !blocksUntilUpgrade;
  }, [blocksUntilUpgrade, notification]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setNotification(null);
    clearPreviewQueryParam();
  }, [clearPreviewQueryParam]);

  const handleDismiss = useCallback(async () => {
    if (!canDismiss) {
      return;
    }

    if (previewMode) {
      closeModal();
      return;
    }

    if (!notification || !userId) {
      closeModal();
      return;
    }

    const timestamp = new Date().toISOString();

    await saveLocalNotificationState(userId, notification.id, {
      dismissedAt: timestamp,
      completedAt: timestamp,
    });
    closeModal();

    try {
      await recordNotificationDismiss(notification.id);
    } catch (error) {
      console.warn('Failed to record notification dismissal:', error);
    }
  }, [canDismiss, closeModal, notification, previewMode, userId]);

  const openNotificationUrl = useCallback(
    async (url: string) => {
      if (url.startsWith('/')) {
        navigate(url);
        return;
      }

      try {
        const parsed = new URL(url);
        if (parsed.origin === window.location.origin) {
          navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`);
          return;
        }
      } catch {
        // Fall through to browser handling for malformed absolute URLs.
      }

      if (isCapacitorNative()) {
        await Browser.open({ url });
      } else {
        window.location.assign(url);
      }
    },
    [navigate],
  );

  const handlePrimaryAction = useCallback(async () => {
    if (previewMode) {
      closeModal();
      return;
    }

    if (!notification || !userId) {
      closeModal();
      return;
    }

    if (!notification.cta_url) {
      if (blocksUntilUpgrade) {
        return;
      }
      await handleDismiss();
      return;
    }

    const timestamp = new Date().toISOString();

    await saveLocalNotificationState(userId, notification.id, {
      ctaClickedAt: timestamp,
      ...(blocksUntilUpgrade ? {} : { completedAt: timestamp }),
    });

    if (!blocksUntilUpgrade) {
      closeModal();
    }

    try {
      await recordNotificationCtaClick(notification.id);
    } catch (error) {
      console.warn('Failed to record notification CTA click:', error);
    }

    await openNotificationUrl(notification.cta_url);
  }, [
    blocksUntilUpgrade,
    closeModal,
    handleDismiss,
    notification,
    openNotificationUrl,
    previewMode,
    userId,
  ]);

  if (!announcementModalEnabled || !notification) {
    return null;
  }

  return (
    <AnnouncementModal
      open={open}
      notification={notification}
      canDismiss={canDismiss}
      blocksUntilUpgrade={blocksUntilUpgrade}
      onDismiss={handleDismiss}
      onPrimaryAction={handlePrimaryAction}
    />
  );
};

export default AnnouncementModalHost;
